import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@ppl/ui';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../services/user.service';
import { Program, Role, UserDetails } from '../../../../interfaces/user.interface';
import { LoggerService } from '../../../../shared/services/logger.service';
import { MenuDropButtonComponent } from "../../../../shared/menu-drop-button/menu-drop-button.component";
import { MenuService } from '../../../../services/menu.service';
import { MainMenuItem } from '../../../../interfaces/menu.interface';
import { RoutingService } from '../../../../shared/services/routing.service';
import { AlertboxComponent, SideDrawerComponent } from '@ppl/ui';
import { ToasterBackupComponent } from "../../../../shared/components/toaster-backup/toaster-backup.component";
import { NotificationsComponent } from "@ppl/ui";
import { environment } from '../../../../../environments/environment';
import { MenuItem } from 'primeng/api';
import { StorageService } from '../../../../services/storage.service';
import { FocusTrapDirective } from '../../../../shared/directives/focus-trap.directive';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { SectionsService } from '../../../../services/sections.service';

@Component({
  selector: 'ppl-header',
  standalone: true,
  imports: [CommonModule,
            MenuDropButtonComponent, 
            NotificationsComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnInit {
  isVisible = false;
  userDetails: UserDetails | undefined;

  isAlertVisible = false;
  isToasterVisible = false;
  isSideDrawerVisible = false;
  userRole: any;
  selectedRoleName: string = '';

  // Role switcher
  availableRoles: Role[] = [];
  isRoleDropdownOpen = false;
  @ViewChild('roleDropdown') roleDropdownRef!: ElementRef;

  isDarkTheme = false;

  handleClose(): void {
    console.log("Alert was closed");
    this.isAlertVisible = false;
  }
  toggleDarkTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    const body = document.body;
    if (this.isDarkTheme) {
      body.classList.add('dark-theme');
    } else {
      body.classList.remove('dark-theme');
    }
  }

  handleToasterClose(): void {
    console.log("Alert was closed");
    this.isToasterVisible = false;
  }

  handleSideDrawerClose(): void {
    console.log("Alert was closed");
    this.isSideDrawerVisible = false;
  }

  menuItemstoDisplay?: MainMenuItem;
  menuSelected: string | undefined;
  selectedMenuItem?: MenuItem;
  selProgramCode?: string;
  menu?: MainMenuItem;
  selectedProgram = '';
  isInternalApp = true;
  disableMenu: boolean = true;
  notifications: any[] = [];

  constructor(private userService: UserService,
    private authService: AuthService, private router: RoutingService, private logger: LoggerService, private menuService: MenuService,
    private storageService: StorageService, private allowListStore: AllowListAuthStore, private sectionService: SectionsService) {
    this.isInternalApp = !environment.isExternal;
  }

  ngOnInit() {
    this.authService.userDetailsSubject$.subscribe((userDetails) => {
      this.userDetails = userDetails?.responsedata;
      this.refreshNotifications();
    });

    this.userService.selectedProgramSubject.subscribe((program: Program) => {
      this.selectedProgram = program.program_name;
      this.selProgramCode = program.program_code;

      // Track available roles for the role switcher
      this.availableRoles = (program.roles || []).filter(Boolean);

      // --- Allow-list model ---
      this.userService.allowedMenusHasChanged.subscribe(allowedMenuIds => {
        if (allowedMenuIds && allowedMenuIds.length > 0) {
          this.disableMenu = false;
          this.menuItemstoDisplay = this.menuService.getAllowedMenus(this.selProgramCode!, allowedMenuIds);
          const basicMenus = this.menuItemstoDisplay?.[this.menuService.getLandingCategory(this.selProgramCode!)];
          this.selectedMenuItem = basicMenus && basicMenus.length > 0 ? basicMenus[0] : undefined;
          if (this.selectedMenuItem) {
            this.storageService.setFirstMenu(this.selectedMenuItem['menu_id'] as string);
          }
          this.refreshNotifications();
        }
      });
    });

    // Subscribe to selected role changes
    this.userService.selectedRoleSubject.subscribe((role) => {
      this.selectedRoleName = role ? this.getDisplayRoleName(role) : '';
    });

    // Initialize with current role if already selected
    const currentRole = this.userService.getSelectedRole();
    if (currentRole) {
      this.selectedRoleName = this.getDisplayRoleName(currentRole);
    }
  }

  private getDisplayRoleName(role: Role): string {
    const hasProvider = this.availableRoles.some(r => r.role_name === 'Provider');
    const hasParticipant = this.availableRoles.some(r => r.role_name === 'Participant');
    if (hasProvider && hasParticipant && role.role_name === 'Provider') {
      return 'Employer';
    }
    return role.role_name;
  }

  manageProfile() {
    console.log('Manage Profile clicked');
  }

  logout(): void {
    this.authService.logout();
  }

  homeRedirection() {
    this.menu = undefined;
    this.selectedProgram = '';
    this.menuItemstoDisplay = undefined;
    this.userService.removeSelectedMenu();
    this.router.navigate(`/`);
  }

  viewAllNotifications(): void {
    // Navigate to notifications page or handle view all logic
    console.log('View all notifications clicked');
    // Example: this.router.navigate(['/home/notifications']);
  }

  // -----------------------------------------------------------------------
  // Role switcher
  // -----------------------------------------------------------------------

  get hasMultipleRoles(): boolean {
    return this.availableRoles.length > 1;
  }

  toggleRoleDropdown(event: Event): void {
    event.stopPropagation();
    this.isRoleDropdownOpen = !this.isRoleDropdownOpen;
  }

  switchRole(role: Role): void {
    this.isRoleDropdownOpen = false;

    // Don't do anything if they picked the same role
    const currentRole = this.userService.getSelectedRole();
    if (currentRole?.role_name === role.role_name) return;

    this.userService.setSelectedRole(role);
    this.selectedRoleName = this.getDisplayRoleName(role);
    this.sectionService.clearTab();

    const programCode = this.selProgramCode!;

    // Load allow-list config for the new role from the API
    this.allowListStore.loadConfig(role.role_sid!, programCode).subscribe(config => {
      if (config) {
        const allowedMenuIds = this.allowListStore.getAllowedMenuIds();
        this.userService.setAllowedMenus(allowedMenuIds);

        this.menuItemstoDisplay = this.menuService.getAllowedMenus(programCode, allowedMenuIds);
        this.disableMenu = false;

        const basicMenus = this.menuItemstoDisplay?.[this.menuService.getLandingCategory(programCode)];
        this.selectedMenuItem = basicMenus && basicMenus.length > 0 ? basicMenus[0] : undefined;
        if (this.selectedMenuItem) {
          this.storageService.setFirstMenu(this.selectedMenuItem['menu_id'] as string);
        }

        // Navigate to first available menu
        const landingComponent = basicMenus && basicMenus.length > 0
          ? basicMenus[0].landing_component
          : 'participantEnrollmentList';
        this.userService.setFirstSegment(landingComponent ?? 'participantEnrollmentList');
        this.router.navigate(`/home/${landingComponent}`);
        this.refreshNotifications();
      } else {
        // Config not found — navigate to error page
        this.router.navigate('/unauthorized');
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isRoleDropdownOpen && this.roleDropdownRef &&
        !this.roleDropdownRef.nativeElement.contains(event.target)) {
      this.isRoleDropdownOpen = false;
    }
  }

  private refreshNotifications() {
    const program = this.selProgramCode;
    const userType = this.userDetails?.usertype_lkp;
    if (!program || !userType) {
      return;
    }
    if (program === 'Pre_Ref') {
      this.notifications = [];
      return;
    }
    this.userService.fetchNotificationMessages(program, userType).subscribe({
      next: (data) => {
        this.notifications = data?.responsedata ?? [];
      },
      error: () => {
        this.notifications = [];
      }
    });
  }
}
